import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import {
  OFFER_STATUS_LABELS,
  formatCreditOldId,
  formatDate,
  formatMoney,
  isQuotaOverdue,
  roundMoney,
} from "./condonationFormat";

/**
 * Ofertas del crédito ("Condonación sobre saldo total"): lista todas las ofertas del crédito con lo que
 * se condona y lo que el cliente pagará por cuota (`amountToPay`). El "Total condonado (activo)" suma solo
 * las ofertas ACTIVAS (PENDING_OFFER); el "Total a pagar para normalización" suma el `amountToPay` de
 * TODAS las ofertas del acuerdo (ofrecidas + aplicadas), porque el valor a pagar acordado con el cliente
 * no debe cambiar a medida que las ofertas se aplican. El backend permite como máximo una oferta activa
 * por cuota, así que no hay doble conteo.
 */
export default function CreditOffersDialog({ open, credit, onClose, onChanged }) {
  const [offers, setOffers] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOffers, setSelectedOffers] = useState({});
  const [canceling, setCanceling] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | CANCELLED | NOT_CANCELLED | PENDING_OFFER | APPLIED
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchOffers = () => {
    if (!credit?.uuid) {
      return;
    }
    setLoading(true);
    // Ofertas (para condonado/ofrecido por cuota) + cuotas activas (para los totales de normalización y
    // saldo total, que necesitan también las cuotas vencidas/no vencidas sin oferta).
    Promise.all([
      axiosClient.get(`/v1/condonations/credits/${credit.uuid}/offers/list`),
      axiosClient.get(`/v1/condonations/credits/${credit.uuid}/quotas`),
    ])
      .then(([offersResponse, quotasResponse]) => {
        setLoading(false);
        setOffers(offersResponse.data || []);
        setQuotas(quotasResponse.data || []);
        setSelectedOffers({});
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "No se pudieron cargar las ofertas",
          text: error.response?.data?.message || "Intenta de nuevo.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  useEffect(() => {
    if (open) {
      setStatusFilter("ALL");
      setDateFrom("");
      setDateTo("");
      fetchOffers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, credit]);

  if (!credit) {
    return null;
  }

  const activeOffers = offers.filter((offer) => offer.status === "PENDING_OFFER");
  const totalCondoned = roundMoney(
    activeOffers.reduce((sum, offer) => sum + Number(offer.totalCondoned || 0), 0)
  );

  // Ofertas que forman el acuerdo con el cliente: ofrecidas (PENDING_OFFER) o ya aplicadas (APPLIED).
  // La normalización suma el amountToPay de TODAS ellas —no solo las pendientes por aplicar— para que el
  // valor a pagar acordado no cambie a medida que las ofertas se van aplicando.
  const agreementOffers = offers.filter(
    (offer) =>
      (offer.status === "PENDING_OFFER" || offer.status === "APPLIED") && offer.amountToPay != null
  );
  const agreementQuotaUuids = new Set(agreementOffers.map((offer) => offer.quotaUuid));

  // Total para normalización: solo cuotas VENCIDAS, con su condonación aplicada (lo que paga el cliente
  // para ponerse al día). Total por saldo total: todas las cuotas activas (incluye no vencidas) = lo que
  // pagaría para matar el crédito.
  const overdueQuotas = quotas.filter((activeQuota) => isQuotaOverdue(activeQuota?.quota));
  // Saldo del crédito: usamos el balanceCredit cacheado del backend; pero es un valor que no siempre
  // viene poblado (puede llegar null/0). En ese caso caemos al saldo vivo = suma de los pendientes de
  // las cuotas activas, para no romper el cálculo (que se iría a negativo al restar lo condonado).
  const quotasPendingTotal = quotas.reduce(
    (sum, activeQuota) => sum + Number(activeQuota?.pending?.totalPending || 0),
    0
  );
  const creditBalance =
    Number(credit.balanceCredit) > 0 ? Number(credit.balanceCredit) : quotasPendingTotal;
  // Total por saldo total = saldo del crédito menos lo condonado activo (nunca negativo).
  const payoffTotal = roundMoney(Math.max(0, creditBalance - totalCondoned));

  // Última cuota PENDING = la de mayor quotaNumber entre las pendientes (no es credit.numeroQuotas:
  // puede haber cuotas PAID después de las pendientes). Si esa última pendiente está vencida, no queda
  // ninguna pendiente por vencer, así que normalizar equivale a pagar todo el saldo = payoffTotal.
  const pendingQuotas = quotas.filter((activeQuota) => activeQuota?.quota?.status === "PENDING");
  const lastPendingQuota = pendingQuotas.reduce(
    (last, activeQuota) =>
      Number(activeQuota?.quota?.quotaNumber || 0) > Number(last?.quota?.quotaNumber || 0)
        ? activeQuota
        : last,
    null
  );
  // Normalización = suma del amountToPay de TODAS las ofertas del acuerdo (ofrecidas + aplicadas) + el
  // pendiente de las cuotas vencidas sin oferta (el cliente igual debe pagarlas para ponerse al día). No
  // se filtra por PENDING_OFFER. Si la última cuota PENDING está vencida no hay cuotas por vencer y
  // normalizar equivale a pagar todo el saldo (payoffTotal).
  const agreementOffersTotal = agreementOffers.reduce(
    (sum, offer) => sum + Number(offer.amountToPay),
    0
  );
  const overdueWithoutOfferTotal = overdueQuotas
    .filter((activeQuota) => !agreementQuotaUuids.has(activeQuota?.quota?.uuid))
    .reduce((sum, activeQuota) => sum + Number(activeQuota?.pending?.totalPending || 0), 0);
  const normalizationTotal = isQuotaOverdue(lastPendingQuota?.quota)
    ? payoffTotal
    : roundMoney(Math.max(0, agreementOffersTotal + overdueWithoutOfferTotal));
  // Abonado a la normalización = ofertas ya aplicadas (el cliente ya pagó esas cuotas del acuerdo). El
  // saldo es lo que falta por abonar para completar la normalización.
  const abonadoNormalizacion = roundMoney(
    agreementOffers
      .filter((offer) => offer.status === "APPLIED")
      .reduce((sum, offer) => sum + Number(offer.amountToPay), 0)
  );
  const saldoNormalizacion = roundMoney(Math.max(0, normalizationTotal - abonadoNormalizacion));

  const handleCancel = (offer) => {
    axiosClient
      .post(`/v1/condonations/offers/${offer.uuid}/cancel`)
      .then(() => {
        fetchOffers();
        if (onChanged) {
          onChanged();
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "No se pudo cancelar la oferta",
          text: error.response?.data?.message || "Intenta de nuevo.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  // Filtros de la tabla (no afectan los totales del crédito, que reflejan todas sus ofertas).
  const matchesStatus = (offer) => {
    if (statusFilter === "NOT_CANCELLED") {
      return offer.status !== "CANCELLED";
    }
    if (statusFilter === "ALL") {
      return true;
    }
    // Estados exactos: CANCELLED | PENDING_OFFER | APPLIED
    return offer.status === statusFilter;
  };
  const matchesDate = (offer) => {
    const offerDate = offer.expirationDate ? String(offer.expirationDate).split("T")[0] : "";
    if (dateFrom && (!offerDate || offerDate < dateFrom)) {
      return false;
    }
    if (dateTo && (!offerDate || offerDate > dateTo)) {
      return false;
    }
    return true;
  };
  const filteredOffers = offers.filter((offer) => matchesStatus(offer) && matchesDate(offer));

  // Solo las ofertas activas (PENDING_OFFER) visibles con el filtro se pueden cancelar/seleccionar.
  const cancelableOffers = filteredOffers.filter((offer) => offer.status === "PENDING_OFFER");
  const selectedUuids = cancelableOffers
    .map((offer) => offer.uuid)
    .filter((uuid) => selectedOffers[uuid]);
  const allSelected = cancelableOffers.length > 0 && selectedUuids.length === cancelableOffers.length;
  const someSelected = selectedUuids.length > 0 && !allSelected;

  const toggleOffer = (uuid) => {
    setSelectedOffers((prev) => ({ ...prev, [uuid]: !prev[uuid] }));
  };

  const toggleAllOffers = () => {
    if (allSelected) {
      setSelectedOffers({});
      return;
    }
    const next = {};
    cancelableOffers.forEach((offer) => {
      next[offer.uuid] = true;
    });
    setSelectedOffers(next);
  };

  const handleBatchCancel = () => {
    const toCancel = cancelableOffers.filter((offer) => selectedOffers[offer.uuid]);
    if (toCancel.length === 0) {
      return;
    }
    Swal.fire({
      icon: "warning",
      title: `¿Cancelar ${toCancel.length} oferta(s)?`,
      text: "Esta acción cancelará las ofertas seleccionadas.",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#161e55",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      setCanceling(true);
      Promise.allSettled(
        toCancel.map((offer) => axiosClient.post(`/v1/condonations/offers/${offer.uuid}/cancel`))
      ).then((results) => {
        setCanceling(false);
        const failed = results.filter((item) => item.status === "rejected").length;
        fetchOffers();
        if (onChanged) {
          onChanged();
        }
        if (failed > 0) {
          Swal.fire({
            icon: failed === toCancel.length ? "error" : "warning",
            title:
              failed === toCancel.length
                ? "No se pudieron cancelar las ofertas"
                : `Se cancelaron ${toCancel.length - failed} de ${toCancel.length}`,
            text: "Algunas ofertas no se pudieron cancelar. Intenta de nuevo.",
            confirmButtonColor: "#161e55",
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Ofertas canceladas",
            text: `Se cancelaron ${toCancel.length} oferta(s).`,
            confirmButtonColor: "#161e55",
          });
        }
      });
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Ofertas del crédito ${formatCreditOldId(credit)}`}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#f4f6fb",
            borderRadius: 2,
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Ofertas activas
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {activeOffers.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total condonado (activo)
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {formatMoney(totalCondoned)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total a pagar para normalización
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#161e55" }}>
              {formatMoney(normalizationTotal)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Cuotas vencidas con su condonación
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              Abonado: <strong>{formatMoney(abonadoNormalizacion)}</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              Saldo: <strong>{formatMoney(saldoNormalizacion)}</strong>
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total a pagar por saldo total
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#161e55" }}>
              {formatMoney(payoffTotal)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Incluye no vencidas · para matar el crédito
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="ALL">Todas</MenuItem>
            <MenuItem value="PENDING_OFFER">Oferta pendiente</MenuItem>
            <MenuItem value="APPLIED">Aplicada</MenuItem>
            <MenuItem value="NOT_CANCELLED">Sin cancelar</MenuItem>
            <MenuItem value="CANCELLED">Canceladas</MenuItem>
          </TextField>
          <TextField
            type="date"
            size="small"
            label="Vencimiento desde"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            label="Vencimiento hasta"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {(statusFilter !== "ALL" || dateFrom || dateTo) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter("ALL");
                setDateFrom("");
                setDateTo("");
              }}
            >
              Limpiar filtros
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
            {`${filteredOffers.length} de ${offers.length} oferta(s)`}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={cancelableOffers.length === 0}
                    onChange={toggleAllOffers}
                    inputProps={{ "aria-label": "Seleccionar todas las ofertas" }}
                  />
                </TableCell>
                <TableCell>Cuota</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Total condonado</TableCell>
                <TableCell>El cliente pagará</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOffers.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2">
                      {offers.length === 0
                        ? "No hay ofertas registradas para este crédito."
                        : "Ninguna oferta coincide con los filtros."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredOffers.map((offer) => (
                <TableRow key={offer.uuid} selected={!!selectedOffers[offer.uuid]}>
                  <TableCell padding="checkbox">
                    {offer.status === "PENDING_OFFER" && (
                      <Checkbox
                        size="small"
                        checked={!!selectedOffers[offer.uuid]}
                        onChange={() => toggleOffer(offer.uuid)}
                        inputProps={{ "aria-label": `Seleccionar oferta cuota ${offer.quotaNumber ?? ""}` }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{offer.quotaNumber ?? "—"}</TableCell>
                  <TableCell>{formatDate(offer.expirationDate)}</TableCell>
                  <TableCell>{OFFER_STATUS_LABELS[offer.status] || offer.status}</TableCell>
                  <TableCell>{formatMoney(offer.totalCondoned)}</TableCell>
                  <TableCell>
                    {offer.amountToPay != null ? formatMoney(offer.amountToPay) : "—"}
                  </TableCell>
                  <TableCell>{offer.reason}</TableCell>
                  <TableCell>
                    {offer.status === "PENDING_OFFER" && (
                      <Button color="error" size="small" onClick={() => handleCancel(offer)}>
                        Cancelar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          color="error"
          variant="contained"
          disabled={selectedUuids.length === 0 || canceling}
          onClick={handleBatchCancel}
        >
          {canceling
            ? "Cancelando..."
            : `Cancelar seleccionadas${selectedUuids.length > 0 ? ` (${selectedUuids.length})` : ""}`}
        </Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
